import {Input, Form, Modal, Col, Row, InputNumber} from "antd";
import {useFormik} from "formik";

import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import {useStore} from "../../../../hooks/useStore";

type ShiftScoreObjectionModalProps = {
    shiftScore: {},
    visibility: Boolean,
    onCancel: () => void,
    closeModal: () => void
};

const ShiftScoreObjectionModal = ({
    shiftScore,
    visibility,
    onCancel,
    closeModal,
    objectionType
}): ShiftScoreObjectionModalProps => {
    const {t} = useTranslation([
        "shiftScore",
        "courierStatuses",
        "courierStatusUpdateModal",
        "changeStatusList"
    ]);
    const stores = useStore();
    const [loading, setLoading] = useState();
    const {
        _id = "",
        reportDate = "",
        fullname = "",
        tckn = "",
        cellPhone = "",
        companyName = "",
        companyCode = "",
        subCompanyName = "",
        subCompanyCode = "",
        shiftStartTime = "",
        shiftEndTime = "",
        startTime = "",
        endTime = "",
        workTimeMinute = "",
        busyTimeMinute = "",
        packageCount = ""
    } = shiftScore || {};

    const formik = useFormik({
        initialValues: {
            text: "",
            shiftReportId: _id,
            workTimeMinute: workTimeMinute,
            packageCount: packageCount
        },
        validationSchema: yup.object().shape({
            text: yup.string().required(t("validation.required", {ns: "common"})),
            workTimeMinute: yup.string().required(t("validation.required", {ns: "common"})),
            packageCount: yup.string().required(t("validation.required", {ns: "common"}))
        }),
        onSubmit: async values => {
            setLoading(true);
            await stores.shiftStore.createShiftScoreObjection(values);
            await resetForm();

            setLoading(false);
            closeModal();
        },
        enableReinitialize: true
    });

    const {values, errors, touched, handleChange, handleSubmit, resetForm, setFieldValue} = formik;

    const labels = {
        updateModal: {
            title: t("shiftScore.createObjectionModal.updateTitle", {ns: "shiftScore"}),
            okText: t("shiftScore.createObjectionModal.updateTitle", {ns: "shiftScore"}),
            workingTime: t("shiftScore.createObjectionModal.updateExpectedWorkTime", {
                ns: "shiftScore"
            }),
            packageCount: t("shiftScore.createObjectionModal.updatePackageCount", {
                ns: "shiftScore"
            }),
            objectionDescription: t("shiftScore.createObjectionModal.updateDescription", {
                ns: "shiftScore"
            })
        },
        objectionModal: {
            title: t("shiftScore.createObjectionModal.title", {ns: "shiftScore"}),
            okText: t("shiftScore.createObjectionModal.title", {ns: "shiftScore"}),
            workingTime: t("fields.expectedWorkTime", {ns: "common"}),
            packageCount: t("fields.expectedPackageCount", {ns: "common"}),
            objectionDescription: t("fields.objectionDescription", {ns: "common"})
        }
    };

    return (
        <Modal
            title={labels[objectionType].title}
            visible={visibility}
            onCancel={onCancel}
            confirmLoading={loading}
            width={630}
            okText={labels[objectionType].okText}
            onOk={handleSubmit}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
            data-testid="createShiftReportObjection"
        >
            <Row justify="space-between">
                <Col>
                    <p>
                        <b>{t("fields.date", {ns: "common"})}</b> : {reportDate}
                    </p>
                    <p>
                        <b>{t("fields.courier", {ns: "common"})}</b> : {fullname}
                    </p>
                    <p>
                        <b>{t("fields.courierIdNo", {ns: "common"})}</b> : {tckn}
                    </p>
                    <p>
                        <b>{t("fields.courierPhone", {ns: "common"})}</b> : {cellPhone}
                    </p>
                    <p>
                        <b>{t("fields.company", {ns: "common"})}</b> : {companyName}
                    </p>
                    <p>
                        <b>{t("fields.companyCode", {ns: "common"})}</b> : {companyCode}
                    </p>
                    <p>
                        <b>{t("fields.subcompany", {ns: "common"})}</b> : {subCompanyName}
                    </p>
                    <p>
                        <b>{t("fields.subCompanyCode", {ns: "common"})}</b> : {subCompanyCode}
                    </p>
                </Col>
                <Col>
                    <p>
                        <b>{t("shiftScore.shiftStartTime", {ns: "shiftScore"})}</b> :{" "}
                        {shiftStartTime}
                    </p>
                    <p>
                        <b>{t("shiftScore.shiftEndTime", {ns: "shiftScore"})}</b> : {shiftEndTime}
                    </p>
                    <p>
                        <b>{t("shiftScore.startTime", {ns: "shiftScore"})}</b> : {startTime}
                    </p>

                    <p>
                        <b>{t("shiftScore.endTime", {ns: "shiftScore"})}</b> : {endTime}
                    </p>
                    <p>
                        <b>
                            {t("shiftScore.workTime", {ns: "shiftScore"})} - (
                            {t("units.minute.short", {ns: "common"})})
                        </b>{" "}
                        : {workTimeMinute}
                    </p>
                    {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                        <>
                            <p>
                                <b>
                                    {t("shiftScore.busyTimeMinute", {ns: "shiftScore"})} - (
                                    {t("units.minute.short", {ns: "common"})})
                                </b>{" "}
                                : {busyTimeMinute}
                            </p>
                        </>
                    )}
                    <p>
                        <b>{t("shiftScore.packageCount", {ns: "shiftScore"})}</b> : {packageCount}
                    </p>
                </Col>

                <Col>
                    <Form autoComplete="off" labelCol={{span: 24}} wrapperCol={{span: 24}}>
                        <Form.Item
                            colon={false}
                            label={`${labels[objectionType].workingTime} :`}
                            labelAlign="left"
                            validateStatus={
                                errors.workTimeMinute && touched.workTimeMinute
                                    ? "error"
                                    : "success"
                            }
                            help={
                                errors.workTimeMinute && touched.workTimeMinute
                                    ? errors.workTimeMinute
                                    : null
                            }
                        >
                            <InputNumber
                                cols={120}
                                rows={4}
                                name="workTimeMinute"
                                value={values.workTimeMinute}
                                onChange={value => {
                                    setFieldValue(`workTimeMinute`, value || 0);
                                }}
                                data-testid="workTimeMinute"
                            />
                        </Form.Item>
                        <Form.Item
                            colon={false}
                            label={`${labels[objectionType].packageCount}:`}
                            labelAlign="left"
                            validateStatus={
                                errors.packageCount && touched.packageCount ? "error" : "success"
                            }
                            help={
                                errors.packageCount && touched.packageCount
                                    ? errors.packageCount
                                    : null
                            }
                        >
                            <InputNumber
                                name="packageCount"
                                value={values.packageCount}
                                onChange={value => {
                                    setFieldValue(`packageCount`, value || 0);
                                }}
                                data-testid="packageCount"
                            />
                        </Form.Item>
                        <Form.Item
                            colon={false}
                            label={`${labels[objectionType].objectionDescription} :`}
                            labelAlign="left"
                            validateStatus={errors.text && touched.text ? "error" : "success"}
                            help={errors.text && touched.text ? errors.text : null}
                        >
                            <Input.TextArea
                                cols={120}
                                rows={4}
                                name="text"
                                value={values.text}
                                onChange={handleChange}
                                data-testid="text"
                            />
                        </Form.Item>
                    </Form>
                </Col>
            </Row>
        </Modal>
    );
};

export default ShiftScoreObjectionModal;
